"use client";

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupLabel,
  useSidebar,
} from "@focal/ui-kit";
import { NavItem } from "@/components/common/AppSidebar/types";
import { SidebarItem } from "@/components/common/AppSidebar/components/SidebarItem";
import { useNavItemClick } from "@/components/common/AppSidebar/components/useNavItemClick";
import AvatarCustom from "@/components/common/AvatarCustom/AvatarCustom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@focal/ui-kit";
import { Dialog, DialogContent, DialogTitle } from "@focal/ui-kit";
import { Icon } from "@focal/ui-kit";
import { WelcomeTutorial } from "@/components/common/WelcomeTutorial";

import React, { useState } from "react";
import { useSessionContext } from "@/context";
import { cn } from "@/lib/utils";
import { FeatureFlagName } from "@/types/feature-flag";

const AccountDropdownNavItem = (item: NavItem) => {
  const onClick = useNavItemClick(item);

  return (
    <DropdownMenuItem onSelect={onClick}>
      {item.icon && (
        <span className="mr-2 flex h-6 w-6 items-center justify-center">
          <Icon name={item.icon} size="md" />
        </span>
      )}
      {item.title}
    </DropdownMenuItem>
  );
};

export function NavUser({
  user,
  items,
  adminItems,
  compact = false,
  pinnedItemUrl,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  items: NavItem[];
  adminItems?: NavItem[];
  compact?: boolean;
  pinnedItemUrl?: string;
}) {
  const { handleLogout, getFeatureFlag } = useSessionContext();
  const { state } = useSidebar();
  const [showTutorial, setShowTutorial] = useState(false);

  const isConsolidated = compact && !!pinnedItemUrl;
  const footerItems = isConsolidated
    ? items.filter((item) => item.url === pinnedItemUrl)
    : items;
  const movedItems = isConsolidated
    ? items.filter((item) => item.url !== pinnedItemUrl)
    : [];
  const hasAdmin = !!adminItems && adminItems.length > 0;
  const showAdminInline = !isConsolidated && hasAdmin;
  const showAdminInDropdown = isConsolidated && hasAdmin;

  return (
    <>
      <SidebarMenu>
        {footerItems.map((item) => (
          <SidebarItem key={item.title} {...item} />
        ))}
      </SidebarMenu>
      {showAdminInline && (
        <SidebarGroup className="p-0" data-cy="admin-nav-section">
          <SidebarGroupLabel className="ml-2">Admin</SidebarGroupLabel>
          <SidebarMenu>
            {adminItems.map((item) => (
              <SidebarItem key={item.title} {...item} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      )}
      {/* FE-1072 (Ian 82bdb13c): the hr baseline should line up with the
          conversation-page footer's top border. The page footer is
          pt-4 (16px) + ~36px content + pb-6 (24px) ≈ 76px from the
          viewport bottom; SidebarFooter's own p-2 (bottom 8px) plus the
          user row height puts the hr close, and `mb-4` (16px below the
          hr) tips it into alignment. */}
      <hr className="w-full mt-2 mb-1" />
      <SidebarMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent flex data-[state=open]:text-sidebar-accent-foreground text-center aria-[expanded=false]:justify-center mb-2",
              )}
            >
              <AvatarCustom name={user.name} className={cn("h-7! w-7!")} />
              {state === "expanded" && (
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={"right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="px-2 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <AvatarCustom name={user.name} className="h-8 w-8" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowTutorial(true);
                }}
              >
                <span className="mr-2 flex h-6 w-6 items-center justify-center">
                  <Icon name="PlayCircleIcon" size="md" />
                </span>
                Welcome tutorial
              </DropdownMenuItem>
            </>
            {(movedItems.length > 0 || showAdminInDropdown) && (
              <>
                <DropdownMenuSeparator />
                {movedItems.map((item) => (
                  <AccountDropdownNavItem key={item.title} {...item} />
                ))}
                {showAdminInDropdown && (
                  <div data-cy="admin-nav-section">
                    <DropdownMenuLabel className="pr-2 pl-4 text-xs font-normal text-typography-muted">
                      Admin
                    </DropdownMenuLabel>
                    {adminItems!.map((item) => (
                      <AccountDropdownNavItem key={item.title} {...item} />
                    ))}
                  </div>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <span className="mr-2 flex h-6 w-6 items-center justify-center">
                <Icon name="LogoutIcon" size="md" />
              </span>
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
          <DialogContent
            className="max-w-none w-screen h-screen p-0 border-0 rounded-none"
            showCloseIcon={false}
          >
            <DialogTitle className="sr-only">Welcome tutorial</DialogTitle>
            <WelcomeTutorial
              onClose={() => setShowTutorial(false)}
              onFinalStep={() => setShowTutorial(false)}
            />
          </DialogContent>
        </Dialog>
      </SidebarMenu>
    </>
  );
}
